import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const StatusPill = styled.span`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    background: ${(p) =>
        p.$tone === 'ok'
            ? palette.pastel.aspargus
            : p.$tone === 'warn'
              ? palette.pastel.orange
              : palette.graphite.cloud};
    color: ${(p) =>
        p.$tone === 'ok'
            ? palette.evergreen.aspargus
            : p.$tone === 'warn'
              ? palette.vibrant.coral
              : palette.graphite.charcoal};
    text-transform: capitalize;
`;

const StepCard = styled.div`
    border: 1px solid ${palette.graphite.silver};
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 10px;
`;

const Snippet = styled.pre`
    background: ${palette.graphite.cloud};
    padding: 12px;
    font-size: 12px;
    overflow: auto;
    border-radius: 4px;
    margin: 6px 0 12px;
`;

const Select = styled.select`
    width: 100%;
    padding: 8px;
    font-size: 13px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    th,
    td {
        text-align: left;
        padding: 10px 8px;
        border-bottom: 1px solid ${palette.graphite.silver};
        vertical-align: top;
    }
    th {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: ${palette.graphite.steel};
    }
    tr {
        cursor: pointer;
    }
    tr:hover td {
        background: ${palette.graphite.cloud};
    }
`;

function setupTone(status) {
    if (status === 'ready') return 'ok';
    if (status === 'action_required' || status === 'needs_setup') return 'warn';
    return 'neutral';
}

function setupLabel(status) {
    if (status === 'ready') return 'Ready';
    if (status === 'action_required') return 'Action required';
    if (status === 'needs_setup') return 'Needs setup';
    return status || '—';
}

function AwarePhishDelivery() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const {
        delivery,
        deliveryProfiles,
        loadingDelivery,
        loadingDeliveryProfiles,
        savingAsset,
    } = awarePhishStore;

    const [selectedId, setSelectedId] = useState(null);
    const [mailPlatform, setMailPlatform] = useState('m365');
    const [deliveryStatus, setDeliveryStatus] = useState('not_started');
    const [reportingStatus, setReportingStatus] = useState('not_started');
    const [reportingMailbox, setReportingMailbox] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState('');
    const [newSenderId, setNewSenderId] = useState('');
    const [newLandingId, setNewLandingId] = useState('');

    const reload = async () => {
        if (!companyId) return;
        await Promise.all([
            awarePhishStore.loadDelivery(companyId),
            awarePhishStore.loadDeliveryProfiles(companyId),
        ]);
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    useEffect(() => {
        if (!delivery) return;
        setMailPlatform(delivery.mailPlatform || 'm365');
        setDeliveryStatus(delivery.deliveryStatus || 'not_started');
        setReportingStatus(delivery.reportingStatus || 'not_started');
        setReportingMailbox(delivery.reportingMailbox || '');
    }, [delivery]);

    useEffect(() => {
        if (!selectedId && deliveryProfiles.length) {
            setSelectedId(deliveryProfiles[0].id);
        }
    }, [deliveryProfiles, selectedId]);

    const selected = useMemo(
        () => deliveryProfiles.find((p) => p.id === selectedId) || null,
        [deliveryProfiles, selectedId]
    );

    const senders = (delivery && delivery.availableSenders) || [];
    const landings = (delivery && delivery.availableLandings) || [];

    const saveTenant = async (extra = {}) => {
        try {
            await awarePhishStore.saveDelivery(companyId, {
                mailPlatform,
                deliveryStatus,
                reportingStatus,
                reportingMailbox: reportingMailbox || null,
                markInstructed: true,
                ...extra,
            });
            commonStore.success('Delivery settings saved');
            await reload();
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save delivery settings'
            );
        }
    };

    const createCustom = async () => {
        try {
            const p = await awarePhishStore.saveDeliveryProfile(
                companyId,
                null,
                {
                    name: newName,
                    senderDomainId: newSenderId,
                    landingDomainId: newLandingId,
                }
            );
            commonStore.success('Delivery profile created');
            setShowCreate(false);
            setNewName('');
            setNewSenderId('');
            setNewLandingId('');
            if (p && p.id) setSelectedId(p.id);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to create profile'
            );
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH delivery</title>
            </Helmet>
            <HeaderSection
                heading="Delivery profiles"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Built-in AwareGO profiles use matching sender and landing domains. Add a custom profile to mix domains. Pick a profile when you create a simulation.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
            />
            <AwarePhishSubNav active="delivery" />

            {(loadingDelivery || loadingDeliveryProfiles) &&
            !deliveryProfiles.length ? (
                <TextDefault lighter>Loading…</TextDefault>
            ) : (
                <>
                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <FlexDiv
                            alignCenter
                            justifyBetween
                            style={{ marginBottom: 8 }}
                        >
                            <Heading3>Profiles</Heading3>
                            <ThemedButton
                                text={showCreate ? 'Cancel' : 'Add custom'}
                                mode="secondary"
                                onClick={() => setShowCreate((v) => !v)}
                                $noWrap
                            />
                        </FlexDiv>

                        {showCreate ? (
                            <div style={{ marginBottom: 16 }}>
                                <FieldLabel>Name</FieldLabel>
                                <input
                                    style={{
                                        width: '100%',
                                        padding: 8,
                                        fontSize: 13,
                                        boxSizing: 'border-box',
                                    }}
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="e.g. Cross-domain pilot"
                                />
                                <FieldLabel>Sender domain</FieldLabel>
                                <Select
                                    value={newSenderId}
                                    onChange={(e) =>
                                        setNewSenderId(e.target.value)
                                    }
                                >
                                    <option value="">Select…</option>
                                    {senders.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.hostname}
                                        </option>
                                    ))}
                                </Select>
                                <FieldLabel>Landing domain</FieldLabel>
                                <Select
                                    value={newLandingId}
                                    onChange={(e) =>
                                        setNewLandingId(e.target.value)
                                    }
                                >
                                    <option value="">Select…</option>
                                    {landings.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.hostname}
                                        </option>
                                    ))}
                                </Select>
                                <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                    <ThemedButton
                                        text={
                                            savingAsset
                                                ? 'Saving…'
                                                : 'Create profile'
                                        }
                                        mode="primary"
                                        disabled={
                                            savingAsset ||
                                            !newName.trim() ||
                                            !newSenderId ||
                                            !newLandingId
                                        }
                                        onClick={createCustom}
                                        $noWrap
                                    />
                                </FlexDiv>
                            </div>
                        ) : null}

                        {!deliveryProfiles.length ? (
                            <TextTiny lighter>
                                No delivery profiles yet. AwareGO builtins
                                appear once sender and landing domains are
                                ready.
                            </TextTiny>
                        ) : (
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Profile</th>
                                        <th>Sender</th>
                                        <th>Landing</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deliveryProfiles.map((p) => (
                                        <tr
                                            key={p.id}
                                            onClick={() => setSelectedId(p.id)}
                                            style={{
                                                background:
                                                    selectedId === p.id
                                                        ? palette.graphite.cloud
                                                        : undefined,
                                            }}
                                        >
                                            <td>
                                                <TextDefault bold>
                                                    {p.name}
                                                </TextDefault>
                                            </td>
                                            <td>
                                                {(p.senderDomain &&
                                                    p.senderDomain.hostname) ||
                                                    '—'}
                                            </td>
                                            <td>
                                                {(p.landingDomain &&
                                                    p.landingDomain.hostname) ||
                                                    '—'}
                                            </td>
                                            <td>
                                                {p.isBuiltin
                                                    ? 'Built-in'
                                                    : 'Custom'}
                                            </td>
                                            <td>
                                                <StatusPill
                                                    $tone={setupTone(
                                                        p.setupStatus
                                                    )}
                                                >
                                                    {setupLabel(p.setupStatus)}
                                                </StatusPill>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Block>

                    {selected ? (
                        <Block padding="20" column style={{ marginBottom: 14 }}>
                            <Heading3>{selected.name}</Heading3>
                            <TextTiny lighter style={{ marginBottom: 10 }}>
                                Sender:{' '}
                                {(selected.senderDomain &&
                                    selected.senderDomain.hostname) ||
                                    '—'}
                                {' · '}
                                Landing: {selected.landingUrl || '—'}
                                {' · '}
                                {selected.isBuiltin ? 'Built-in' : 'Custom'}
                            </TextTiny>

                            <StatusPill $tone={setupTone(selected.setupStatus)}>
                                {setupLabel(selected.setupStatus)}
                            </StatusPill>

                            {(selected.blockers || []).length ? (
                                <div style={{ marginTop: 12 }}>
                                    <FieldLabel>Blockers</FieldLabel>
                                    {(selected.blockers || []).map((b) => (
                                        <StepCard key={b.id}>
                                            <TextDefault bold>
                                                {b.title}
                                            </TextDefault>
                                            <TextTiny lighter>
                                                {b.detail}
                                            </TextTiny>
                                        </StepCard>
                                    ))}
                                </div>
                            ) : null}

                            {(selected.nextSteps || []).length ? (
                                <div style={{ marginTop: 8 }}>
                                    <FieldLabel>Next</FieldLabel>
                                    {(selected.nextSteps || []).map((s) => (
                                        <StepCard key={s.id}>
                                            <TextDefault bold>
                                                {s.title}
                                            </TextDefault>
                                            <TextTiny lighter>
                                                {s.detail}
                                            </TextTiny>
                                        </StepCard>
                                    ))}
                                </div>
                            ) : null}

                            {selected.instructions ? (
                                <div style={{ marginTop: 8 }}>
                                    <Heading3>
                                        {selected.instructions.title}
                                    </Heading3>
                                    <TextTiny
                                        lighter
                                        style={{ marginBottom: 10 }}
                                    >
                                        Domains:{' '}
                                        {selected.instructions.senderDomain ||
                                            '—'}{' '}
                                        · IPs:{' '}
                                        {(
                                            selected.instructions.sendingIps ||
                                            []
                                        ).join(', ') || '—'}{' '}
                                        · Landing:{' '}
                                        {selected.instructions.landingUrl ||
                                            '—'}
                                    </TextTiny>
                                    {(selected.instructions.steps || []).map(
                                        (s) => (
                                            <StepCard key={s.id}>
                                                <TextDefault bold>
                                                    {s.title}
                                                </TextDefault>
                                                <TextTiny lighter>
                                                    {s.detail}
                                                </TextTiny>
                                            </StepCard>
                                        )
                                    )}
                                    {selected.instructions.snippets &&
                                    selected.instructions.snippets
                                        .powershell ? (
                                        <>
                                            <FieldLabel>
                                                PowerShell helper
                                            </FieldLabel>
                                            <Snippet>
                                                {
                                                    selected.instructions
                                                        .snippets.powershell
                                                }
                                            </Snippet>
                                        </>
                                    ) : null}
                                </div>
                            ) : null}

                            {!selected.isBuiltin ? (
                                <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                    <ThemedButton
                                        text="Delete custom profile"
                                        mode="secondary"
                                        disabled={savingAsset}
                                        onClick={async () => {
                                            try {
                                                await awarePhishStore.removeDeliveryProfile(
                                                    companyId,
                                                    selected.id
                                                );
                                                setSelectedId(null);
                                                commonStore.success(
                                                    'Profile deleted'
                                                );
                                            } catch (e) {
                                                commonStore.error(
                                                    awarePhishStore.error ||
                                                        'Delete failed'
                                                );
                                            }
                                        }}
                                        $noWrap
                                    />
                                </FlexDiv>
                            ) : null}
                        </Block>
                    ) : null}

                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <Heading3>Organisation setup</Heading3>
                        <TextTiny lighter style={{ marginBottom: 8 }}>
                            Shared for all profiles: mail platform, DKIM
                            identity, and reporting.
                        </TextTiny>

                        <FieldLabel>Mail platform</FieldLabel>
                        <Select
                            value={mailPlatform}
                            onChange={(e) => setMailPlatform(e.target.value)}
                        >
                            <option value="m365">Microsoft 365</option>
                            <option value="google">Google Workspace</option>
                            <option value="other">Other / on-prem</option>
                        </Select>

                        <FieldLabel>Delivery status</FieldLabel>
                        <Select
                            value={deliveryStatus}
                            onChange={(e) => setDeliveryStatus(e.target.value)}
                        >
                            <option value="not_started">Not started</option>
                            <option value="instructed">Instructed</option>
                            <option value="configured">Configured</option>
                            <option value="verified">Verified</option>
                            <option value="failed">Failed</option>
                        </Select>

                        <FieldLabel>Reporting status</FieldLabel>
                        <Select
                            value={reportingStatus}
                            onChange={(e) => setReportingStatus(e.target.value)}
                        >
                            <option value="not_started">Not started</option>
                            <option value="instructed">Instructed</option>
                            <option value="configured">Configured</option>
                            <option value="verified">Verified</option>
                            <option value="failed">Failed</option>
                        </Select>

                        <FieldLabel>
                            Reporting mailbox (optional, Microsoft 365)
                        </FieldLabel>
                        <input
                            style={{
                                width: '100%',
                                padding: 8,
                                fontSize: 13,
                                boxSizing: 'border-box',
                            }}
                            value={reportingMailbox}
                            onChange={(e) =>
                                setReportingMailbox(e.target.value)
                            }
                            placeholder="phishing-reports@yourcompany.com"
                        />

                        <FlexDiv gap={12} style={{ marginTop: 16 }} $noWrap>
                            <ThemedButton
                                text={savingAsset ? 'Saving…' : 'Save'}
                                mode="primary"
                                onClick={() => saveTenant()}
                                disabled={savingAsset}
                                $noWrap
                            />
                            <ThemedButton
                                text="Mark delivery verified"
                                mode="secondary"
                                onClick={() =>
                                    saveTenant({ deliveryStatus: 'verified' })
                                }
                                disabled={savingAsset}
                                $noWrap
                            />
                        </FlexDiv>
                    </Block>

                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <Heading3>DKIM identity</Heading3>
                        <TextTiny lighter style={{ marginBottom: 8 }}>
                            Your organisation’s DKIM domain for Microsoft 365
                            Advanced Delivery. Add it with the sending IPs from
                            the selected profile.
                        </TextTiny>
                        <TextDefault>
                            {(delivery && delivery.dkimHostname) ||
                                'Not issued yet'}
                        </TextDefault>
                        {delivery && delivery.dkimAvailable ? (
                            <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                <ThemedButton
                                    text={
                                        savingAsset
                                            ? 'Working…'
                                            : delivery.dkimHostname
                                              ? 'Re-issue DKIM hostname'
                                              : 'Issue DKIM hostname'
                                    }
                                    mode="secondary"
                                    disabled={savingAsset}
                                    onClick={async () => {
                                        try {
                                            const r =
                                                await awarePhishStore.issueDkim(
                                                    companyId
                                                );
                                            commonStore.success(
                                                `DKIM ready: ${
                                                    (r &&
                                                        r.issuedDkimHostname) ||
                                                    (r && r.dkimHostname) ||
                                                    'issued'
                                                }`
                                            );
                                            await reload();
                                        } catch (e) {
                                            commonStore.error(
                                                awarePhishStore.error ||
                                                    'Could not issue DKIM identity'
                                            );
                                        }
                                    }}
                                    $noWrap
                                />
                            </FlexDiv>
                        ) : !(delivery && delivery.dkimHostname) ? (
                            <TextTiny lighter style={{ marginTop: 8 }}>
                                DKIM identity is not available yet. AwareGO will
                                enable it for your organisation.
                            </TextTiny>
                        ) : null}
                    </Block>

                    <Block padding="20" column>
                        <Heading3>Report phishing sync (Microsoft 365)</Heading3>
                        <TextTiny lighter style={{ marginBottom: 8 }}>
                            Reads your reporting mailbox and marks matching
                            simulation recipients as Reported.
                        </TextTiny>
                        <ThemedButton
                            text={
                                savingAsset ? 'Syncing…' : 'Sync reports now'
                            }
                            mode="primary"
                            disabled={
                                savingAsset ||
                                !(delivery && delivery.reportingMailbox)
                            }
                            onClick={async () => {
                                try {
                                    const r =
                                        await awarePhishStore.syncReports(
                                            companyId
                                        );
                                    commonStore.success(
                                        `Scanned ${(r && r.scanned) || 0}, reported ${(r && r.reported) || 0}, skipped ${(r && r.skipped) || 0}`
                                    );
                                } catch (e) {
                                    commonStore.error(
                                        awarePhishStore.error ||
                                            'Report sync failed'
                                    );
                                }
                            }}
                            $noWrap
                        />
                    </Block>
                </>
            )}
        </Container>
    );
}

export default observer(AwarePhishDelivery);
